import { z } from 'zod';

import { apiClient } from './api-client';

const REFRESH_TOKEN_KEY = 'hrms_refresh_token';

const AuthUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  tenantId: z.string(),
  roles: z.array(z.string()),
  mfaEnabled: z.boolean(),
  /**
   * The tenant's enabled module keys. An `agency` key switches the shell to
   * the agency product — see `deriveAgencyNav`.
   *
   * zod STRIPS unknown keys by default, so omitting this does not merely fail
   * to type the field: it silently deletes it from the parsed response, and
   * the symptom is a correct server payload producing an unchanged UI.
   *
   * Optional because a server that predates migration 0079 does not send it,
   * and absent must read as "not an agency" rather than crash the login.
   */
  modules: z.array(z.string()).optional(),
});

const AuthTokensSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  tokenType: z.literal('Bearer'),
  accessTokenExpiresAt: z.string(),
  refreshTokenExpiresAt: z.string(),
});

const LoginResponseSchema = z.discriminatedUnion('mfaRequired', [
  z.object({
    mfaRequired: z.literal(false),
    user: AuthUserSchema,
    tokens: AuthTokensSchema,
  }),
  z.object({
    mfaRequired: z.literal(true),
    mfaChallengeToken: z.string(),
  }),
]);

// Server /auth/refresh returns the token pair directly (flat), not wrapped.
const RefreshResponseSchema = AuthTokensSchema;

export type AuthUser = z.infer<typeof AuthUserSchema>;
export type AuthTokens = z.infer<typeof AuthTokensSchema>;
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

export function getStoredRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function storeRefreshToken(token: string): void {
  localStorage.setItem(REFRESH_TOKEN_KEY, token);
}

export function removeRefreshToken(): void {
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

export async function loginApi(
  email: string,
  password: string,
  tenantSlug: string,
): Promise<LoginResponse> {
  return apiClient.post('/auth/login', { email, password, tenantSlug }, LoginResponseSchema);
}

export async function logoutApi(accessToken: string): Promise<void> {
  await apiClient.post('/auth/logout', {}, z.unknown(), {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export async function refreshApi(refreshToken: string, signal?: AbortSignal): Promise<AuthTokens> {
  return apiClient.post(
    '/auth/refresh',
    { refreshToken },
    RefreshResponseSchema,
    signal ? { signal } : undefined,
  );
}
